import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  ClipboardCheck, Calendar, Home, LogOut, User, Settings, Menu, X,
  Building2, Clock, CheckCircle, FileText, Download, Phone
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VisitareDashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [visitador, setVisitador] = useState(null);
  const [agenda, setAgenda] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('visitare_token');
    const userData = localStorage.getItem('visitare_user');
    
    if (!token) {
      navigate('/visitare/login');
      return;
    }

    setUser(JSON.parse(userData));
    loadData(token);
  }, [navigate]);

  const loadData = async (token) => {
    try {
      const [visitadorRes, agendaRes] = await Promise.all([
        fetch(`${API_URL}/api/visitare/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/visitare/minha-agenda`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!visitadorRes.ok || !agendaRes.ok) {
        throw new Error('Sessão expirada');
      }

      setVisitador(await visitadorRes.json());
      setAgenda(await agendaRes.json());
    } catch (error) {
      toast.error('Sessão expirada. Faça login novamente.');
      handleLogout();
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('visitare_token');
    localStorage.removeItem('visitare_user');
    navigate('/visitare/login');
  };

  const NavLink = ({ to, icon: Icon, children }) => {
    const isActive = location.pathname === to;
    return (
      <Link
        to={to}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
          isActive 
            ? 'bg-[#0066CC] text-white' 
            : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
        }`}
      >
        <Icon className="w-5 h-5" />
        <span>{children}</span>
      </Link>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  // If we're at the base dashboard path, show the dashboard content
  const isDashboardHome = location.pathname === '/visitare/dashboard';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white dark:bg-slate-900 border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-6 h-6 text-[#0066CC]" />
          <span className="font-bold">VISITARE</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-white dark:bg-slate-900 border-r transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#0066CC] rounded-xl flex items-center justify-center">
              <ClipboardCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg">VISITARE</h1>
              <p className="text-xs text-slate-500">Portal de Visitadores</p>
            </div>
          </div>

          <nav className="space-y-1">
            <NavLink to="/visitare/dashboard" icon={Home}>Dashboard</NavLink>
            <NavLink to="/visitare/agenda" icon={Calendar}>Minha Agenda</NavLink>
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-slate-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{visitador?.nome}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <Button variant="outline" className="w-full" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        {isDashboardHome ? (
          <div className="p-6 space-y-6">
            {/* Welcome */}
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                Olá, {visitador?.nome?.split(' ')[0]}!
              </h1>
              <p className="text-slate-500">Bem-vindo ao Portal Visitare</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Visitas Pendentes</p>
                      <p className="text-3xl font-bold text-amber-600">{agenda.filter(v => v.status === 'Agendada').length}</p>
                    </div>
                    <Calendar className="w-10 h-10 text-amber-500 opacity-30" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Em Curso</p>
                      <p className="text-3xl font-bold text-blue-600">{agenda.filter(v => v.status === 'Em Curso').length}</p>
                    </div>
                    <Clock className="w-10 h-10 text-blue-500 opacity-30" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Total Realizadas</p>
                      <p className="text-3xl font-bold text-green-600">{visitador?.total_visitas || 0}</p>
                    </div>
                    <CheckCircle className="w-10 h-10 text-green-500 opacity-30" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Próximas Visitas */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Próximas Visitas</CardTitle>
              </CardHeader>
              <CardContent>
                {agenda.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Não tem visitas agendadas</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {agenda.slice(0, 5).map(visita => (
                      <div key={visita.id} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            visita.tipo_visita === 'Telefónica' 
                              ? 'bg-purple-100' 
                              : 'bg-[#0066CC]/10'
                          }`}>
                            {visita.tipo_visita === 'Telefónica' 
                              ? <Phone className="w-6 h-6 text-purple-600" />
                              : <Building2 className="w-6 h-6 text-[#0066CC]" />
                            }
                          </div>
                          <div>
                            <p className="font-medium">{visita.instituicao_nome}</p>
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Calendar className="w-3 h-3" />
                              {visita.data_agendada}
                              {visita.hora_agendada && ` às ${visita.hora_agendada}`}
                              <Badge variant="outline" className="text-xs">
                                {visita.tipo_visita || 'Presencial'}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={visita.status === 'Em Curso' ? 'default' : visita.status === 'Concluída' ? 'success' : 'secondary'}>
                            {visita.status}
                          </Badge>
                          {visita.status === 'Concluída' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                window.open(`${API_URL}/api/visitare/visitas/${visita.id}/pdf`, '_blank');
                              }}
                              title="Descarregar Relatório PDF"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                          {visita.status !== 'Concluída' && (
                            <Link to={`/visitare/visita/${visita.id}`}>
                              <Button size="sm" className="bg-[#0066CC]">
                                {visita.status === 'Em Curso' ? 'Continuar' : 'Iniciar'}
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {agenda.length > 5 && (
                  <div className="mt-4 text-center">
                    <Link to="/visitare/agenda">
                      <Button variant="outline">Ver Toda a Agenda</Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          <Outlet context={{ visitador, agenda, loadData: () => loadData(localStorage.getItem('visitare_token')) }} />
        )}
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
